import { useQuery } from '@tanstack/react-query';
import PaymentsAPIServiceV2, {
    AvailableShifts,
    CurrentPeriodShifts,
    GetManualPosJournalsParams
} from './PaymentsServiceV2';
import { GetZenegyUsersDetailedReqest } from '@/models/payment';
import { useUser } from '@/contexts/user/UserContext';

const paymentsService = PaymentsAPIServiceV2.getInstance();

export const PAYMENT_QUERY_KEYS: { [key: string]: string } = {
    TIME_REGISTRATION_PROVIDERS: 'getTimeRegistrationProviders',
    SALARY_PROVIDERS: 'getSalaryProviders',
    EMPLOYEE_TYPES: 'getEmployeeTypes',
    EMPLOYEE_AUTO_APPROVAL_TYPE_SETTINGS: 'getEmployeeAutoApprovalTypeSettings',
    DEPARTMENTS: 'getDepartments',
    USER: 'getUser',
    syncBankTransactionRecords: 'syncBankTransactionRecords',
    getAllBankTransactionReports: 'getAllBankTransactionReports',
    getSelectableHouseAccounts: 'getSelectableHouseAccounts',
    getCompanyHouseAccountBinding: 'getCompanyHouseAccountBinding',
    getHouseAccountConnectionStatus: 'getHouseAccountConnectionStatus',
    getHouseAccountBankMaterial: 'getHouseAccountBankMaterial',
    getTransactionBankDetails: 'getTransactionBankDetails',
    getBankTransactionRecordDetails: 'getBankTransactionRecordDetails',
    getCompanyAccounts: 'getCompanyAccounts',
    getLast10Jobs: 'getLast10Jobs',
    getImportFilters: 'getImportFilters',
    getAllIntegrationStatus: 'getAllIntegrationStatus',
    getPendingTransactions: 'getPendingTransactions',
    getDanLonConnectURL: 'getDanLonConnectURL',
    getDanLonExchange: 'getDanLonExchange',
    getMasterCardConnectURL: 'getMasterCardConnectURL',
    getCurrentCompanyDetails: 'getCurrentCompanyDetails',
    getPOSLocationsAndDepartments: 'getPOSLocationsAndDepartments',
    getAllPOSDepartments: 'getAllPOSDepartments',
    getPOSJournalLines: 'getPOSJournalLines',
    getManualPosJournals: 'getManualPosJournals',
    getTrivecGroupFilters: 'getTrivecGroupFilters',
    getPOSEmployeeGroup: 'getPOSEmployeeGroup',
    getPOSProviders: 'getPOSProviders',
    getUserTips: 'getUserTips',
    getTipsExclusions: 'getTipsExclusions',
    getDepartmentSalaryTypes: 'getDepartmentSalaryTypes',
    getPayrollStatus: 'getPayrollStatus',
    getUserPastTips: 'getUserPastTips',
    getEmploymentGroupTemplates: 'getEmploymentGroupTemplates',
    getTipsHistory: 'getTipsHistory',
    getEmployeeDocuments: 'getEmployeeDocuments',
    getUserCompanies: 'getUserCompanies',
    getAdminCompanies: 'getAdminCompanies',
    ZENEGY_DEPARTMENTS: 'getZenegyDepartments',
    ZENEGY_SALARY_MODES: 'getZenegySalaryModes',
    ZENEGY_SALARY_TYPES: 'getZenegySalaryTypes',
    ZENEGY_EMPLOYEE_TYPES: 'getZenegyEmployeeTypes',
    INTECT_DEPARTMENTS: 'getIntectDepartments',
    INTECT_EMPLOYMENT_TEMPLATES: 'getIntectEmploymentTemplates',
    GET_UPCOMING_SHIFTS: 'getUpcomingShifts',
    GET_AVAILABLE_SHIFTS: 'getAvailableShifts'
};

export const useTimeRegistrationProvidersQuery = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.TIME_REGISTRATION_PROVIDERS, enabled],
        queryFn: paymentsService.getTimeRegistrationProviders,
        enabled
    });

export const useSalaryProvidersQuery = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.SALARY_PROVIDERS, enabled],
        queryFn: paymentsService.getSalaryProviders,
        enabled
    });

export const useEmployeeTypes = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.EMPLOYEE_TYPES, enabled],
        queryFn: paymentsService.getEmployeeTypes,
        enabled
    });

export const useEmployeeAutoApprovalTypesQuery = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.EMPLOYEE_AUTO_APPROVAL_TYPE_SETTINGS],
        queryFn: paymentsService.getEmployeeAutoApprovalTypes,
        enabled
    });

export const useEmployeeDepartments = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.DEPARTMENTS, enabled],
        queryFn: paymentsService.getEmployeeDepartments,
        enabled
    });

export const useGetUserQuery = ({
    userId,
    enabled
}: {
    userId?: string;
    enabled: boolean;
}) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.USER, userId],
        queryFn: () => paymentsService.getUser(userId!),
        enabled: !!userId && enabled
    });
};

export const useGetAllIntegrationStatus = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAllIntegrationStatus, enabled],
        queryFn: paymentsService.getAllIntegrationStatus,
        enabled
    });

export const useGetHouseAccounts = (
    enabled: boolean = true,
    includeInactive: boolean = false
) =>
    useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getSelectableHouseAccounts,
            includeInactive
        ],
        queryFn: () =>
            paymentsService.getSelectableHouseAccounts(includeInactive),
        enabled
    });

// companyId scopes the cache per company; the request itself resolves the
// company from the header. Keep disabled for non-house-account companies so we
// never query a binding that cannot exist.
export const useCompanyHouseAccountBinding = (
    companyId?: string,
    enabled: boolean = true
) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getCompanyHouseAccountBinding, companyId],
        queryFn: () => paymentsService.getCompanyHouseAccountBinding(companyId),
        enabled
    });

export const useGetHouseAccountConnectionStatus = (
    instaPaidHouseAccountId?: string
) =>
    useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getHouseAccountConnectionStatus,
            instaPaidHouseAccountId
        ],
        queryFn: () =>
            paymentsService.getHouseAccountAiiaConnectionStatus(
                instaPaidHouseAccountId!
            ),
        // Operator-infrastructure state must not be requested until an account is
        // picked: keep the query disabled without a selection.
        enabled: !!instaPaidHouseAccountId
    });

// Current bank material for the edit dialog, used to pre-fill the editable
// fields. Kept disabled until a dialog opens for a concrete account so a closed
// dialog never triggers the Key Vault-backed fetch.
export const useGetHouseAccountBankMaterial = (
    instaPaidHouseAccountId?: string,
    enabled: boolean = true
) =>
    useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getHouseAccountBankMaterial,
            instaPaidHouseAccountId
        ],
        queryFn: () =>
            paymentsService.getHouseAccountBankMaterial(
                instaPaidHouseAccountId!
            ),
        enabled: enabled && !!instaPaidHouseAccountId
    });

export const useGetAllBankTransactionReports = ({
    instaPaidHouseAccountId,
    enabled = true,
    limit,
    offset,
    statuses
}: {
    instaPaidHouseAccountId?: string;
    enabled?: boolean;
    limit?: number;
    offset?: number;
    statuses?: string[];
}) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getAllBankTransactionReports,
            instaPaidHouseAccountId,
            limit,
            offset,
            statuses
        ],
        queryFn: () => {
            // refetch() bypasses `enabled`, so guard here too: without an
            // account there is no meaningful request to make.
            if (!instaPaidHouseAccountId) {
                return Promise.reject(
                    new Error('instaPaidHouseAccountId is required')
                );
            }
            return paymentsService.getAllBankTransactionRecords(
                instaPaidHouseAccountId,
                statuses,
                limit,
                offset
            );
        },
        enabled: enabled && !!instaPaidHouseAccountId
    });
};

export const useSyncSyncBankTransactionRecords = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.syncBankTransactionRecords, enabled],
        queryFn: paymentsService.getSyncBankTransactionRecords
    });
};

export const useGetTransactionBankDetails = (
    bankTransactionRecordId: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getTransactionBankDetails,
            bankTransactionRecordId,
            enabled
        ],
        queryFn: () =>
            paymentsService.getTransactionBankDetails(bankTransactionRecordId),
        enabled
    });
};

export const useGetBankTransactionRecordDetails = (
    bankTransactionRecordId: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getBankTransactionRecordDetails,
            bankTransactionRecordId,
            enabled
        ],
        queryFn: () =>
            paymentsService.getBankTransactionRecordDetails(
                bankTransactionRecordId
            ),
        enabled: enabled && !!bankTransactionRecordId
    });
};

export const useGetCompanyAccounts = (enabled: boolean = true) => {
    return useQuery({
        queryKey: ['getCompanyAccounts', enabled],
        queryFn: paymentsService.getCompanyAccounts
    });
};

export const useGetImportFilters = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getImportFilters, enabled],
        queryFn: paymentsService.getImportFilters
    });
};

export const useGetImportFiltersForSource = (
    sourceType: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getImportFilters, sourceType, enabled],
        queryFn: () => paymentsService.getImportFiltersForSource(sourceType),
        enabled: enabled && !!sourceType
    });
};

export const useGetJobStatus = (jobId: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: ['getJobStatus', jobId, enabled],
        queryFn: () => paymentsService.getJobStatus(jobId),
        enabled
    });
};

export const useGetLastJobs = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getLast10Jobs, enabled],
        queryFn: () => paymentsService.getLastJobs()
    });
};

/**
 * Reduces a GET parameter object to the fields that actually reach the URL.
 *
 * `buildGetQuery` skips falsy non-boolean values and emits nothing for an empty
 * array, so `{ limit: 16 }` and a fully-populated object whose filters are all
 * empty produce the *same* request. Keying on the raw object made those two look
 * like different queries, so the employees list was fetched twice on one page
 * load — once for the header count, once for the table.
 */
const requestKey = (request?: object) => {
    if (!request) {
        return JSON.stringify(request);
    }
    const significant = Object.entries(request)
        .filter(([, value]) => {
            if (typeof value === 'boolean') return true;
            if (Array.isArray(value)) return value.length > 0;
            return !!value;
        })
        .sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(Object.fromEntries(significant));
};

export const useGetEmployees = (
    request?: GetZenegyUsersDetailedReqest,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getUsersDetails', requestKey(request)],
        queryFn: () =>
            paymentsService.getUsersDetails<GetZenegyUsersDetailedReqest>(
                request
            ),
        enabled
    });
};

export const useCompanyBalance = (enabled: boolean = true) => {
    const { company } = useUser();
    const companyId = company?.companyId;
    return useQuery({
        queryKey: ['getCompanyBalance', companyId],
        queryFn: paymentsService.getCurrentCompanyBalance,
        enabled: !!companyId && enabled
    });
};

export const useGetCompanyUsers = (
    limit: number,
    offset: number,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getCompanyUsers', limit, offset],
        queryFn: () => paymentsService.getCompanyUsers(limit, offset),
        enabled
    });
};

export const useGetPendingTransactions = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPendingTransactions, enabled],
        queryFn: () => paymentsService.getPendingTransactions(),
        enabled
    });
};

export const useDanLonConnectURL = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getDanLonConnectURL, enabled],
        queryFn: () => paymentsService.getDanLonConnectURL(),
        enabled
    });
};

export const useCurrentCompanyDetails = (enabled: boolean = true) => {
    const { company } = useUser();
    const companyId = company?.companyId;
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getCurrentCompanyDetails, companyId],
        queryFn: () => paymentsService.getCurrentCompany(),
        // Around a dozen components read this, and they mount at staggered times as
        // the page fills in. Whichever ones mount after the first request resolves
        // would each trigger another identical fetch, so the request count depended
        // on a race with the response. Nothing else is suppressed: the key changes
        // on company switch, PageLayout drops the cache then too, four mutations
        // invalidate this key, and Settings/ImportDialog refetch explicitly.
        refetchOnMount: false,
        enabled: !!companyId && enabled
    });
};

export const useMasterCardConnectURL = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getMasterCardConnectURL, enabled],
        queryFn: () => paymentsService.getMasterCardConnectUrl(),
        enabled
    });
};

export const useDanLonExchange = (code: string, enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getDanLonExchange, enabled],
        queryFn: () => paymentsService.dalonExchange(code)
    });
};

export const useGetPOSLocationsAndDepartments = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSLocationsAndDepartments, enabled],
        queryFn: () => paymentsService.getPOSLocationsAndDepartments(),
        enabled
    });
};

export const useGetManualPosJournals = (
    params: GetManualPosJournalsParams = {},
    enabled: boolean = true
) => {
    const { locationId, from, to, offset, limit } = params;
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getManualPosJournals,
            locationId ?? 'all',
            from ?? '',
            to ?? '',
            offset ?? 0,
            limit ?? 'all'
        ],
        queryFn: () => paymentsService.getManualPosJournals(params),
        enabled
    });
};

export const useGetAllPOSDepartments = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAllPOSDepartments, enabled],
        queryFn: paymentsService.getAllPOSDepartments,
        enabled
    });

export const useGetPOSJournalLines = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSJournalLines, enabled],
        queryFn: paymentsService.getPOSJournalLines,
        enabled
    });

export const useGetTrivecGroupFilters = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getTrivecGroupFilters, enabled],
        queryFn: paymentsService.getTrivecGroupFilters,
        enabled
    });

export const useGetEmployeeGroup = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSEmployeeGroup, enabled],
        queryFn: paymentsService.getTipsEmployeeGroup,
        enabled
    });

export const useGetPOSProviders = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPOSProviders, enabled],
        queryFn: paymentsService.getPOSProviders,
        enabled
    });

export const useGetUserTips = (userId: string, enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getUserTips, userId, enabled],
        queryFn: () => paymentsService.getUserTips(userId),
        enabled
    });

export const useGetTipsExclusions = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getTipsExclusions, enabled],
        queryFn: paymentsService.getTipsExclusions,
        enabled
    });

export const useGetDepartmentSalaryTypes = (enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getDepartmentSalaryTypes, enabled],
        queryFn: paymentsService.getDepartmentSalaryTypes,
        enabled
    });

export const useGetPayrollStatus = (userId: string, enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getPayrollStatus, userId, enabled],
        queryFn: async () => {
            const response = await paymentsService.getPayrollStatus(userId);
            return response.data;
        },
        enabled
    });

export const useGetUserPastTips = ({
    userId,
    startDate,
    endDate,
    offset,
    limit,
    enabled = true
}: {
    userId: string;
    startDate?: string;
    endDate?: string;
    offset?: number;
    limit?: number;
    enabled?: boolean;
}) => {
    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getUserPastTips,
            userId,
            startDate,
            endDate,
            offset,
            limit
        ],
        queryFn: () =>
            paymentsService.getUserPastTips(
                userId,
                startDate,
                endDate,
                offset,
                limit
            ),
        enabled
    });
};

export const useTipsHistory = (
    offset: number,
    limit: number,
    locationIds: string[],
    statuses?: string[],
    reportingStatuses?: string[],
    enabled: boolean = true
) => {
    // Normalised so an omitted filter and an explicitly empty one land on the same
    // cache entry. Both serialise to no query parameter, but `undefined` and `[]`
    // are distinct queryKeys — which silently split one request into two when the
    // InstaTips tab bar and the settlement list request the same unfiltered page.
    const statusFilter = statuses ?? [];
    const reportingStatusFilter = reportingStatuses ?? [];

    return useQuery({
        queryKey: [
            PAYMENT_QUERY_KEYS.getTipsHistory,
            enabled,
            offset,
            limit,
            locationIds,
            statusFilter,
            reportingStatusFilter
        ],
        queryFn: ({ signal }) =>
            paymentsService.getTipsHistory({
                offset,
                limit,
                locationIds,
                statuses: statusFilter,
                reportingStatuses: reportingStatusFilter,
                signal
            }),
        enabled
    });
};

export const useTipsHistoryDetails = (
    posLocationId: string,
    date: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getTipsHistoryDetails', enabled, posLocationId, date],
        queryFn: () =>
            paymentsService.getTipsHistoryDetails({
                posLocationId,
                date
            })
    });
};

export const useTipsHistoryExcelDownload = (
    locationIds: string[],
    statuses: string[],
    reportingStatuses: string[],
    enabled: boolean = false
) => {
    return useQuery({
        queryKey: [
            'getTipsHistoryDownload',
            locationIds,
            statuses,
            reportingStatuses,
            enabled
        ],
        queryFn: () =>
            paymentsService.getTipsHistoryExcelDownload({
                locationIds,
                statuses,
                reportingStatuses
            }),
        enabled: enabled
    });
};

export const useTipsReconciliationReport = (
    month: string,
    year: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getTipsReconciliation', month, year, enabled],
        queryFn: () => paymentsService.getTipsReconciliationReport(month, year),
        enabled
    });
};

export const useEstimatedTipsReconciliationReport = (
    month: string,
    year: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: ['getEstimatedTipsReconciliation', month, year, enabled],
        queryFn: () =>
            paymentsService.getEstimatedTipsReconciliationReport(month, year),
        enabled
    });
};

export const useActivePayrollPeriods = (enabled: boolean = true) => {
    return useQuery({
        queryKey: ['getActivePayrollPeriods', enabled],
        queryFn: () => paymentsService.getActivePayrollPeriods(),
        enabled
    });
};

export const useGetEmploymentGroupTemplates = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getEmploymentGroupTemplates, enabled],
        queryFn: () => paymentsService.getEmploymentGroupTemplates(),
        enabled
    });
};

export const useTransactionStatistics = (enabled: boolean = true) => {
    const { company } = useUser();
    const companyId = company?.companyId;
    return useQuery({
        queryKey: ['getTransactionStatistics', companyId],
        queryFn: paymentsService.getTransactionStatistics,
        enabled: !!companyId && enabled
    });
};

export const useGetEmployeeDocuments = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getEmployeeDocuments, enabled],
        queryFn: paymentsService.getEmployeeDocuments,
        enabled
    });
};

export const useGetDocumentImage = (
    url: string,
    pageNumber: number,
    enabled: boolean
) => {
    return useQuery({
        queryKey: ['getDocumentImage', url, pageNumber],
        queryFn: () =>
            paymentsService.generateDocumentImage({ url, pageNumber }),
        enabled
    });
};

export const useGetSignatures = (
    limit: number = 16,
    offset: number = 0,
    searchQuery?: string,
    sortField?: string,
    sortDirection?: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [
            'getSignatures',
            limit,
            offset,
            searchQuery,
            sortField,
            sortDirection
        ],
        queryFn: () =>
            paymentsService.getSignatures(
                limit,
                offset,
                searchQuery,
                sortField,
                sortDirection
            ),
        enabled
    });
};

export const useGetUserCompanies = (
    userId: string,
    enabled: boolean = true
) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getUserCompanies, userId],
        queryFn: () => paymentsService.getCompanyByUserId(userId),
        enabled: enabled && !!userId,
        select: (response) =>
            response?.data?.companies?.map((company: any) => ({
                id: company.companyId,
                name: company.name,
                role: company?.role
            })) || []
    });
};

export const useGetAdminCompanies = (enabled: boolean = true) => {
    return useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.getAdminCompanies],
        queryFn: () => paymentsService.getAdminCompanies(),
        enabled,
        select: (response) =>
            response?.data?.companies
                ?.map((company: any) => ({
                    id: company.companyId,
                    name: company.name
                }))
                .sort((a: any, b: any) => a.name?.localeCompare(b.name)) || []
    });
};

export const useGetUpcomingShifts = (userId: string, enabled: boolean = true) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.GET_UPCOMING_SHIFTS, userId],
        queryFn: async () => {
            const response = await paymentsService.getUpcomingShifts(userId);
            return response.data.currentPeriodShifts as CurrentPeriodShifts;
        },
        enabled: !!userId && enabled
    });

export const useGetAvailableShifts = (
    userId: string,
    enabled: boolean = true
) =>
    useQuery({
        queryKey: [PAYMENT_QUERY_KEYS.GET_AVAILABLE_SHIFTS, userId],
        queryFn: async () => {
            const response = await paymentsService.getAvailableShifts(userId);
            return response.data.availableShifts as AvailableShifts;
        },
        enabled: !!userId && enabled
    });
